// API Configuration for Frontend - Multi-Tenant Support
import axios from 'axios';
import { clearAuthData } from '../utils/tokenUtils';

import { safeStorage } from '../utils/safeStorage';
import { authRedirect } from '../utils/authRedirect';
import { PRODUCTION_API_BASE_URL } from './apiOrigin';
// Multi-tenant organization detection
const getOrganizationInfo = () => {
  // Extract organization from hostname/subdomain
  const hostname = window.location.hostname;
  
  // Check for subdomain pattern (org.evvapp.com)
  const subdomainMatch = hostname.match(/^([^.]+)\.evvapp\.com$/);
  if (subdomainMatch) {
    return {
      subdomain: subdomainMatch[1],
      isProduction: true
    };
  }
  
  // Check for Azure subdomain pattern
  const azureMatch = hostname.match(/^([^-]+)-frontend\..*\.azurecontainerapps\.io$/);
  if (azureMatch) {
    return {
      subdomain: azureMatch[1],
      isProduction: true
    };
  }
  
  // Development - check for organization in localStorage or default to wecare
  const devOrg = safeStorage.getItem('dev_organization') || 'wecare';
  return {
    subdomain: devOrg,
    isProduction: false
  };
};

// Environment-aware API base URL configuration
const getApiBaseUrl = () => {
  // Get organization info for tenant-aware API
  const orgInfo = getOrganizationInfo();
  
  // 1. Explicit environment variable (highest priority) - for custom deployments
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // 2. Auto-detect Azure production environment
  if (window.location.hostname.includes('azurecontainerapps.io') || window.location.hostname.includes('arasynchealth.com')) {
    return PRODUCTION_API_BASE_URL;
  }
  
  // 4. Auto-detect if we're on a custom domain (production)
  if (window.location.protocol === 'https:' && !window.location.hostname.includes('localhost')) {
    // For production deployments, try to infer backend URL
    return `${window.location.protocol}//${window.location.hostname.replace('frontend', 'backend')}/api`;
  }
  
  // 5. Development mode - use localhost backend (allows frontend development)
  if (import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3002/api';
  }
  
  // 6. Fallback for local development
  return 'http://localhost:3002/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and organization info
api.interceptors.request.use(
  async (config) => {
    // Import here to avoid circular dependency
    const { getValidTokenWithRefresh } = await import('../utils/tokenUtils');
    
    const token = await getValidTokenWithRefresh(); // Use enhanced token validation with refresh
    const orgInfo = getOrganizationInfo();
    
    // If no valid token and this is a protected route, reject the request
    if (!token && config.url && !config.url.includes('/login') && !config.url.includes('/public')) {
      console.warn('No valid token available after refresh attempt, redirecting to login');
      clearAuthData();
      authRedirect('/login', 'no_token');
      return Promise.reject(new Error('Authentication required'));
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization context for tenant isolation
    if (orgInfo.subdomain) {
      config.headers['X-Organization-Subdomain'] = orgInfo.subdomain;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and auto-logout
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.warn('🔒 Authentication failed - clearing all auth data');
      clearAuthData();
      
      // Show user-friendly message before redirect
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('Your session has expired. Please log in again.');
      }
      
      authRedirect('/login', 'expired');

      return Promise.reject(new Error('Session expired'));
    }
    
    // Handle token expiry specifically
    if (error.response?.data?.error?.includes('jwt expired') || 
        error.response?.data?.error?.includes('token expired') ||
        error.response?.data?.message?.includes('jwt expired')) {
      console.warn('⏰ JWT token expired - auto-logout triggered');
      clearAuthData();
      
      // Show specific expiry message
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('🔐 Your login session has expired. You will be redirected to the login page.');
      }
      
      authRedirect('/login', 'token_expired');

      return Promise.reject(new Error('Token expired'));
    }
    
    // Handle other 403 errors that might indicate token issues
    if (error.response?.status === 403 && 
        (error.response?.data?.error?.includes('token') || 
         error.response?.data?.error?.includes('authorization'))) {
      console.warn('🚫 Token authorization failed - auto-logout triggered');
      clearAuthData();
      
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('Your session is no longer valid. Please log in again.');
      }
      
      authRedirect('/login', 'unauthorized');

      return Promise.reject(new Error('Authorization failed'));
    }
    
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL };
